import { Users } from "lucide-react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAgencyContacts } from "@/hooks/use-agency";

interface AgencyContactDialogProps {
  agentNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AgencyContactDialog({ agentNumber, open, onOpenChange }: AgencyContactDialogProps) {
  const {
    data: contacts,
    isLoading,
    isError,
    error,
    refetch,
  } = useAgencyContacts(agentNumber, open);

  const typedContacts = contacts ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Agency Contact</DialogTitle>
          <DialogDescription>Agent {agentNumber}</DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load contacts"
          isEmpty={!isLoading && !isError && typedContacts.length === 0}
          emptyIcon={Users}
          emptyMessage="No contacts found for this agency."
          skeletonHeight="h-10"
        />

        {typedContacts.length > 0 && (
          <div className="max-h-[60vh] overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2 font-medium">Name</th>
                  <th className="px-2 py-2 font-medium">Title</th>
                  <th className="px-2 py-2 font-medium">Email</th>
                  <th className="px-2 py-2 font-medium">Phone</th>
                  <th className="px-2 py-2 font-medium">Fax</th>
                  <th className="px-2 py-2 font-medium">Type</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {typedContacts.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b transition-colors last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-2 py-2 font-medium">{c.contactName}</td>
                    <td className="px-2 py-2">{c.contactTitle ?? "—"}</td>
                    <td className="px-2 py-2">
                      {c.contactEmail ? (
                        <a
                          href={`mailto:${c.contactEmail}`}
                          className="text-primary hover:underline"
                        >
                          {c.contactEmail}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {c.contactPhone ? (
                        <a href={`tel:${c.contactPhone}`} className="text-primary hover:underline">
                          {c.contactPhone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-2">{c.contactFax ?? "—"}</td>
                    <td className="px-2 py-2">{c.contactType ?? "—"}</td>
                    <td className="px-2 py-2">
                      <StatusBadge
                        statusKey={c.isActive ? "policy-active" : "policy-expired"}
                        label={c.isActive ? "Active" : "Inactive"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-muted-foreground text-xs">
              Showing {typedContacts.length} contact{typedContacts.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
